import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TableModule } from "primeng/table";
import { ClientListDto } from "../../../../core/models/program/clientListDto";
import { DefaultClientProgramMapRequest } from "../../../../core/models/program/defaultClientProgramMapRequest";
import { ProgramClientMapDto } from "../../../../core/models/program/programClientMap";
import { LoaderService } from "../../../../core/services/loader.service";
import { ProgramsService } from "../../../../core/services/programs.service";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { CommonModule } from "@angular/common";
import { CheckboxChangeEvent, CheckboxModule } from "primeng/checkbox";
import { CustomerAccountComponent } from "../customer-account/customer-account.component";
import { SpinnerComponent } from "../../../../shared/components/spinner/spinner.component";
import { FormsModule } from "@angular/forms";

@UntilDestroy()
@Component({
  selector: "jni-client-list",
  templateUrl: "./client-list.component.html",
  styleUrls: ["./client-list.component.scss"],
  imports: [
    TableModule,
    CommonModule,
    CheckboxModule,
    CustomerAccountComponent,
    SpinnerComponent,
    FormsModule,
  ],
})
export class ClientListComponent implements OnInit, OnChanges {
  clientList: ClientListDto[] = [];
  selectedClientList: ClientListDto[] = [];
  smallLoader = false;
  customerAccount = "";
  isClientAddAllowed = false;
  organizationNameCharacterCond = false;
  clientRemoveList: string[] = [];
  @Input() programId = 0;
  @Input() isClientLockCheck = false;
  @Output() clientSummaryModalEvent = new EventEmitter<string>();

  constructor(
    private _programService: ProgramsService,
    private _toastr: JniToastrService,
    private _loaderService: LoaderService
  ) {}

  checkClientAdd() {
    if (this.isClientLockCheck) {
      this.isClientAddAllowed = true;
    } else {
      this.isClientAddAllowed = false;
    }
  }

  ngOnInit() {
    this.checkClientAdd();
    this.getClientList();
  }

  getCustomerAccount($event: string) {
    this.customerAccount = $event;
  }

  ngOnChanges() {
    this.checkClientAdd();
  }

  onAddClientRelation() {
    this.smallLoader = true;
    if (!this.customerAccount || this.customerAccount === "") {
      this.smallLoader = false;
      this._toastr.error("Error", "Enter a Customer to Add");
      return;
    }

    const ifAlreadyExists = this.clientList.findIndex(
      x => x.customerAccount === this.customerAccount
    );
    if (ifAlreadyExists != -1) {
      this.smallLoader = false;
      this._toastr.error("Error", "Client is already exist in the list");
      return;
    }

    const mapper: ProgramClientMapDto = {
      clientId: this.customerAccount,
      programId: this.programId,
    };
    this._programService
      .createProgramClientRelation(mapper)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.smallLoader = false;
          if (res.data) {
            this.customerAccount = "";
            this._toastr.success("Success", "Client Successfully Added");
            this.getClientList();
          }
        },
        error: res => {
          this.smallLoader = false;
          this._toastr.error("Error", res.message || "Failed to Add Record");
        },
      });
  }

  onRemoveClientRelation() {
    this.smallLoader = true;
    if (this.clientRemoveList.length <= 0) {
      this.smallLoader = false;
      this._toastr.error("Error", "Select a customer to remove");
      return;
    }

    const mapper: ProgramClientMapDto[] = [];
    this.clientRemoveList.forEach(x => {
      mapper.push({
        programId: this.programId,
        clientId: x,
      });
    });
    this._programService
      .RemoveProgramClientRelation(mapper)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.smallLoader = false;
          if (res.data) {
            this._toastr.success("Success", "Clients Successfully removed");
            this.getClientList();
          }
        },
        error: res => {
          this.smallLoader = false;
          this._toastr.success("Error", res.message || "Failed to Add Record");
        },
      });
  }

  getClientList() {
    this.smallLoader = true;
    this.clientList = [];
    this.clientRemoveList = [];
    this._programService
      .getClientList(this.programId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.smallLoader = false;
          if (res.data) {
            if (res.data.length > 0) {
              this.clientList = res.data;
            }
          }
        },
        error: res => {
          this.smallLoader = false;
          this._toastr.error("Error", res.message || "Data not found");
        },
      });
  }

  getSmallLoaderStatus($event: boolean) {
    this.smallLoader = $event;
  }

  checkboxSelection($event: string) {
    if (this.clientRemoveList.includes($event)) {
      this.clientRemoveList.splice(this.clientRemoveList.indexOf($event), 1);
    } else {
      this.clientRemoveList.push($event);
    }
  }

  organizationNameCharacterCount(obj: string) {
    if (obj && obj?.length > 40) this.organizationNameCharacterCond = true;
  }

  onDefaultToogleChange(obj: CheckboxChangeEvent, customerAccount: string) {
    const target = obj.checked as boolean;
    this._loaderService.setLoaderState(true);
    const request: DefaultClientProgramMapRequest = {
      customerAccount: customerAccount,
      defaultClient: target,
      programId: this.programId,
    };

    this._programService
      .defaultClientProgramMap(request)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this._loaderService.setLoaderState(false);
          if (res && res?.status === "Success") {
            this._toastr.success(
              "Success",
              res?.message || "Value is updated successfully"
            );
          } else {
            this._toastr.error(
              "Error",
              res?.message || "Value is updated successfully"
            );
            obj.checked = false;
          }
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", res.message || "Data not found");
        },
      });
  }

  openClientSummaryModal(clientAccount: string) {
    this.clientSummaryModalEvent.emit(clientAccount ?? "");
  }
}
